(() => {
  if (window.vonelaEntrance || !window.IntersectionObserver || !Element.prototype.animate) return;
  window.vonelaEntrance = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  // Mobile overflow can inflate innerHeight beyond the real visual viewport.
  const viewportHeight = () => Math.min(document.documentElement.clientHeight || innerHeight, window.visualViewport?.height || innerHeight);
  const viewportInset = Math.max(0, innerHeight - viewportHeight());
  const seen = new WeakSet();
  const pending = new Set();
  const active = new Map();
  const protectedArea = 'header,footer,nav,form,dialog,[role="dialog"],cart-drawer,.drawer,.vp-main,.vp-sticky,.heart-home__hero,.section-vonela-heart-hero,.shopify-app-block,.scroll-trigger,[data-vonela-reveal],.facets,.mobile-facets,.pagination-wrapper';
  const selector = '.heart-home__section-heading,.heart-home__benefit,.heart-home__step,.heart-home__trust-card,.heart-about-centered__text,.heart-faq-vonela__heading,.vonela-reviews__header,.vonela-review,.nwf-card,.nwf-split-copy,.nwf-split-media,.nwf-split > .nwf-image,.product-card-wrapper,.collection-card-wrapper,.article-card-wrapper,main h2,main .rte p,main .shopify-policy__body p';
  const finish = (element) => {
    active.get(element)?.cancel();
    active.delete(element);
    element.removeAttribute('data-vonela-entrance-active');
    pending.delete(element);
    seen.add(element);
    prepareObserver.unobserve(element);
    entryObserver.unobserve(element);
  };
  const settings = () => {
    const styles = getComputedStyle(document.documentElement);
    return {
      duration: Number(styles.getPropertyValue('--vonela-entrance-duration')) || 0,
      distance: styles.getPropertyValue('--vonela-entrance-distance').trim(),
      stagger: Number(styles.getPropertyValue('--vonela-entrance-stagger')) || 0
    };
  };
  const frames = (distance) => [
    { opacity: 0, translate: `0 ${distance}` },
    { opacity: 1, translate: '0 0px' }
  ];
  const track = (element, animation, phase) => {
    element.setAttribute('data-vonela-entrance-active', phase);
    active.set(element, animation);
    animation.onfinish = () => { if (active.get(element) === animation) finish(element); };
  };
  // Arm just OUTSIDE the viewport. This short, self-finishing animation is
  // also the fail-open: even a later JS exception cannot strand hidden content.
  const prepareObserver = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting, boundingClientRect }) => {
      if (!isIntersecting) return;
      prepareObserver.unobserve(target);
      const { duration, distance } = settings();
      if (reduced.matches || !duration || boundingClientRect.bottom <= 0 || target.contains(document.activeElement)) { finish(target); return; }
      const animation = target.animate([
        { opacity: 0, translate: `0 ${distance}`, offset: 0 },
        { opacity: 0, translate: `0 ${distance}`, offset: 0.8 },
        { opacity: 1, translate: '0 0px', offset: 1 }
      ], { duration: 3000, fill: 'none' });
      track(target, animation, 'prepare');
      entryObserver.observe(target);
    });
  }, { rootMargin: `0px 0px ${64 - viewportInset}px 0px`, threshold: 0 });
  // Wait until a meaningful strip is inside the viewport, rather than spending
  // nearly the whole entrance on the first few pixels below the fold.
  const entryObserver = new IntersectionObserver((entries) => {
    let index = 0;
    const { duration, distance, stagger } = settings();
    entries.forEach(({ target, isIntersecting, boundingClientRect }) => {
      if (!isIntersecting) return;
      if (reduced.matches || !duration || boundingClientRect.bottom <= 0 || target.contains(document.activeElement) || target.matches(':target') || target.closest(':target')) { finish(target); return; }
      active.get(target)?.cancel();
      prepareObserver.unobserve(target);
      entryObserver.unobserve(target);
      pending.delete(target);
      seen.add(target);
      const animation = target.animate(frames(distance), {
        duration, delay: Math.min(index++ * stagger, 100),
        easing: 'cubic-bezier(.2,.45,.25,1)', fill: 'backwards'
      });
      track(target, animation, 'reveal');
    });
  }, { rootMargin: `0px 0px -${viewportInset + Math.min(240, Math.round(viewportHeight() * 0.28))}px 0px`, threshold: 0 });
  const scan = () => {
    if (reduced.matches) return;
    const main = document.querySelector('main');
    if (!main || /\/(cart|checkout|account)(\/|$)/.test(location.pathname)) return;
    const candidates = [...main.querySelectorAll(selector)].filter((element) => {
      if (element.closest(protectedArea) || element.matches('button,a,input,select,textarea,summary') || element.querySelector('form,dialog,[role="dialog"]')) return false;
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      if (!rect.width || !rect.height || style.visibility === 'hidden' || style.display === 'none' || style.animationName !== 'none') return false;
      for (let parent = element; parent && parent !== main; parent = parent.parentElement) {
        if (['fixed', 'sticky'].includes(getComputedStyle(parent).position)) return false;
      }
      return true;
    });
    const selected = new Set(candidates);
    candidates.forEach((element) => {
      for (let parent = element.parentElement; parent && parent !== main; parent = parent.parentElement) {
        if (selected.has(parent)) return;
      }
      if (seen.has(element) || pending.has(element)) return;
      if (element.getBoundingClientRect().top < viewportHeight() || element.contains(document.activeElement) || element.closest(':target')) { seen.add(element); return; }
      pending.add(element);
      prepareObserver.observe(element);
    });
  };
  const stopAll = () => { [...pending, ...active.keys()].forEach(finish); };
  reduced.addEventListener('change', () => { if (reduced.matches) stopAll(); else scan(); });
  document.addEventListener('focusin', ({ target }) => { [...pending, ...active.keys()].forEach((element) => { if (element.contains(target)) finish(element); }); });
  window.addEventListener('hashchange', stopAll);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', ({ target }) => { [...pending, ...active.keys()].forEach((element) => { if (target.contains(element)) finish(element); }); });
  document.addEventListener('shopify:section:select', stopAll);
  document.addEventListener('shopify:block:select', stopAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
